import { useState, type FormEvent } from 'react'
import { Loader2, Mail, AlertCircle } from 'lucide-react'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import type { CreateImapAccountInput, EmailAccountDto } from '@toga/shared'

interface Props {
  onClose: () => void
  onCreated: (account: EmailAccountDto) => void
}

interface Preset {
  label: string
  imapHost: string
  imapPort: number
  imapUseTls: boolean
  smtpHost: string
  smtpPort: number
  hint?: string
}

const PRESETS: Preset[] = [
  {
    label: 'Gmail (app password)',
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    imapUseTls: true,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 465,
    hint: 'Necesitas 2FA activado y un "app password" (Google → seguridad → contraseñas de aplicación).',
  },
  {
    label: 'Outlook / Hotmail',
    imapHost: 'outlook.office365.com',
    imapPort: 993,
    imapUseTls: true,
    smtpHost: 'smtp.office365.com',
    smtpPort: 587,
    hint: 'Para Exchange 365 corporativo, usa F3.9 (Microsoft Graph) en lugar de IMAP.',
  },
  {
    label: 'iCloud Mail',
    imapHost: 'imap.mail.me.com',
    imapPort: 993,
    imapUseTls: true,
    smtpHost: 'smtp.mail.me.com',
    smtpPort: 587,
    hint: 'Requiere app password generado en appleid.apple.com.',
  },
  {
    label: 'Personalizado',
    imapHost: '',
    imapPort: 993,
    imapUseTls: true,
    smtpHost: '',
    smtpPort: 587,
  },
]

export default function NewAccountModal({ onClose, onCreated }: Props) {
  const [presetIndex, setPresetIndex] = useState(0)
  const preset = PRESETS[presetIndex]!
  const [emailAddress, setEmailAddress] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [imapHost, setImapHost] = useState(preset.imapHost)
  const [imapPort, setImapPort] = useState(String(preset.imapPort))
  const [initialSyncDays, setInitialSyncDays] = useState('90')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onPickPreset = (idx: number) => {
    const p = PRESETS[idx]!
    setPresetIndex(idx)
    setImapHost(p.imapHost)
    setImapPort(String(p.imapPort))
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload: CreateImapAccountInput = {
        emailAddress,
        displayName: displayName || null,
        imapHost,
        imapPort: Number(imapPort),
        imapUseTls: preset.imapUseTls,
        smtpHost: preset.smtpHost || null,
        smtpPort: preset.smtpHost ? preset.smtpPort : null,
        smtpUseTls: preset.imapUseTls,
        password,
        initialSyncDays: Number(initialSyncDays),
      }
      const { account } = await api.post<{ account: EmailAccountDto }>(
        '/email-accounts/imap',
        payload
      )
      onCreated(account)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError('Error de red')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal open onClose={onClose} title="Conectar cuenta de correo (IMAP)" maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">Proveedor</label>
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((p, i) => (
              <button
                key={p.label}
                type="button"
                onClick={() => onPickPreset(i)}
                className={[
                  'px-3 py-2 rounded-lg text-sm border transition text-left',
                  i === presetIndex
                    ? 'bg-toga-500/15 border-toga-500/40 text-toga-200'
                    : 'bg-slate-900/40 border-slate-700/50 t-secondary hover:bg-slate-900/60',
                ].join(' ')}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset.hint && (
            <p className="mt-2 text-xs t-muted flex items-start gap-1.5">
              <AlertCircle size={12} className="mt-0.5 shrink-0 text-amber-300" />
              {preset.hint}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">Email</label>
            <input
              type="email"
              required
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="tu@ejemplo.com"
              className={inputCls}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">Nombre a mostrar (opcional)</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Mi correo profesional"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Servidor IMAP</label>
            <input
              required
              value={imapHost}
              onChange={(e) => setImapHost(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Puerto</label>
            <input
              type="number"
              required
              value={imapPort}
              onChange={(e) => setImapPort(e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">Contraseña / App password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Sincronizar últimos (días)</label>
            <input
              type="number"
              min={1}
              max={3650}
              value={initialSyncDays}
              onChange={(e) => setInitialSyncDays(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <p className="text-xs t-faint flex items-start gap-1.5">
          <Mail size={12} className="mt-0.5 shrink-0" />
          Verificaremos la conexión antes de guardar. La contraseña se almacena cifrada
          (AES-256-GCM).
        </p>

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Conectar
          </button>
        </div>
      </form>
    </Modal>
  )
}
